import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserSettings } from '../types/auth';
import { generateKeyPair } from '../utils/encryption';

interface UserSettingsStore {
  // ユーザー設定
  userSettings: UserSettings | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  setUserSettings: (settings: UserSettings) => void;
  updateUserSettings: (updates: Partial<UserSettings>) => void;
  setSecurityMode: (enabled: boolean) => Promise<boolean>;
  generateNewKeyPair: () => Promise<boolean>;
  exportSettings: () => string;
  importSettings: (settingsJson: string) => boolean;
  resetSettings: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

const initialUserSettings: UserSettings = {
  username: '',
  email: '',
  displayName: '',
  securityMode: false,
  publicKey: undefined,
  privateKey: undefined,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

export const useUserSettingsStore = create<UserSettingsStore>()(
  persist(
    (set, get) => ({
      userSettings: null,
      isLoading: false,
      error: null,

      setUserSettings: (settings) => {
        set({
          userSettings: {
            ...settings,
            updatedAt: new Date().toISOString(),
          },
          error: null,
        });
      },

      updateUserSettings: (updates) => {
        const current = get().userSettings;
        if (current) {
          set({
            userSettings: {
              ...current,
              ...updates,
              updatedAt: new Date().toISOString(),
            },
            error: null,
          });
        }
      },

      setSecurityMode: async (enabled) => {
        const { userSettings, generateNewKeyPair, setLoading, setError } = get();
        
        setLoading(true);
        setError(null);

        try {
          if (enabled && userSettings) {
            // セキュリティモードを有効にする場合、鍵ペアが必要
            if (!userSettings.publicKey || !userSettings.privateKey) {
              const success = await generateNewKeyPair();
              if (!success) {
                setLoading(false);
                return false;
              }
            }
          }

          // セキュリティモードの設定を更新
          if (userSettings) {
            set({
              userSettings: {
                ...userSettings,
                securityMode: enabled,
                updatedAt: new Date().toISOString(),
              },
              isLoading: false,
              error: null,
            });
            return true;
          }

          setError('ユーザー設定が見つかりません');
          setLoading(false);
          return false;
        } catch (error) {
          console.error('セキュリティモード設定エラー:', error);
          setError('セキュリティモードの設定に失敗しました');
          setLoading(false);
          return false;
        }
      },

      generateNewKeyPair: async () => {
        const { userSettings, setLoading, setError } = get();
        
        setLoading(true);
        setError(null);

        try {
          const keyPair = await generateKeyPair();
          
          if (userSettings) {
            set({
              userSettings: {
                ...userSettings,
                publicKey: keyPair.publicKey,
                privateKey: keyPair.privateKey,
                updatedAt: new Date().toISOString(),
              },
              isLoading: false,
              error: null,
            });
            return true;
          }

          setError('ユーザー設定が見つかりません');
          setLoading(false);
          return false;
        } catch (error) {
          console.error('鍵ペア生成エラー:', error);
          setError('鍵ペアの生成に失敗しました');
          setLoading(false);
          return false;
        }
      },

      exportSettings: () => {
        const { userSettings } = get();
        if (userSettings) {
          return JSON.stringify(userSettings, null, 2);
        }
        return '';
      },

      importSettings: (settingsJson) => {
        try {
          const settings = JSON.parse(settingsJson) as UserSettings;
          
          // 基本的な検証
          if (!settings.username || !settings.email) {
            throw new Error('必須フィールドが不足しています');
          }

          set({
            userSettings: {
              ...settings,
              updatedAt: new Date().toISOString(),
            },
            error: null,
          });
          return true;
        } catch (error) {
          console.error('設定インポートエラー:', error);
          set({ error: '設定のインポートに失敗しました' });
          return false;
        }
      },

      resetSettings: () => {
        set({
          userSettings: {
            ...initialUserSettings,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          error: null,
        });
      },

      setLoading: (loading) => {
        set({ isLoading: loading });
      },

      setError: (error) => {
        set({ error, isLoading: false });
      },
    }),
    {
      name: 'evaloid-user-settings',
      partialize: (state) => ({
        userSettings: state.userSettings,
      }),
    }
  )
);

// セレクター関数
export const useUserSettings = () => useUserSettingsStore(state => state.userSettings);
export const useSecurityMode = () => useUserSettingsStore(state => state.userSettings?.securityMode || false);
export const useUserSettingsLoading = () => useUserSettingsStore(state => state.isLoading);
export const useUserSettingsError = () => useUserSettingsStore(state => state.error);